/**
 * The edit list: the one language of PithPlay's editing engine
 * (PITHPLAY_EDITING_FEATURES_PLAN.md, section 6.1).
 *
 * A creator's edit of one video (or one multicam set) is a JSON document of
 * operations. The app's editor builds it with manual controls, the AI command
 * bar proposes changes to it, the browser previews it without rendering, and the
 * compiler (`compiler.ts`) turns it into one FFmpeg command for an export.
 * Everything that reads or writes one goes through this module, so the four
 * never disagree.
 *
 * Two clocks:
 * - `segments` are in SOURCE time (milliseconds of the video file, or of the
 *   multicam set's shared timeline). They are the kept stretches, in output
 *   order: trims, cuts and joins are all "which stretches, in what order".
 * - everything placed on the result (overlays, text, captions, music, voiceover)
 *   is in OUTPUT time: milliseconds of the exported video, which is what the
 *   creator sees on the preview's timeline. Removing a stretch shifts what
 *   follows it, as in any editor.
 *
 * This file is copied, unchanged, into the app
 * (`pithplay-rephoenix/src/utils/editing/editList.ts`); a test keeps the two in
 * step. It therefore sticks to what the app's TypeScript 4.9 / ES5 build
 * accepts: no iteration of a Map or Set, no generators, no Node imports.
 */

// ---------------------------------------------------------------------------
// Limits (the shape of a valid list; the plan's allowances live in editingConfig.ts)
// ---------------------------------------------------------------------------

export const EDIT_LIST_VERSION = 1 as const;

export const EDIT_LIMITS = {
  /** Kept stretches per edit. A long talk cut to the good parts is a few dozen. */
  maxSegments: 500,
  maxOverlays: 20,
  maxTexts: 100,
  maxVoiceovers: 50,
  /** Camera switches in a multicam cut (the Live cut allows 2,500 per set). */
  maxMulticamCuts: 2500,
  /** Characters in one text or lower-third line. */
  maxTextLength: 200,
  /** Characters spoken by TTS in one voiceover. */
  maxTtsTextLength: 2000,
  maxTitleLength: 120,
  /** Shortest kept stretch: a frame at 25 fps. */
  minSegmentMs: 40,
  /** Longest source the engine edits: four hours, like transcripts. */
  maxSourceMs: 4 * 60 * 60 * 1000,
  speed: { min: 0.25, max: 4 },
  /** An overlay's width, as a share of the frame's width. */
  overlayWidthPct: { min: 1, max: 100 },
  /** Text height as a share of the frame's height. */
  textSizePct: { min: 1.5, max: 20 },
  gainDb: { min: -60, max: 12 },
  duckDb: { min: 0, max: 40 },
  maxFadeMs: 30000,
  idPattern: /^[A-Za-z0-9_-]{1,32}$/,
  colorPattern: /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/,
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AngleKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
export const ANGLE_KEYS: AngleKey[] = ['A', 'B', 'C', 'D', 'E', 'F'];

export type EditSource =
  | { kind: 'video'; videoId: string }
  | { kind: 'multicam'; multicamSetId: string };

/** The result's frame. 'source' keeps the source's proportions. */
export type OutputAspect = 'source' | '16:9' | '9:16' | '1:1' | '4:5';
export const OUTPUT_ASPECTS: OutputAspect[] = ['source', '16:9', '9:16', '1:1', '4:5'];

/** The result's height (or width, when it's taller than wide). */
export type OutputResolution = '720p' | '1080p' | '2160p';
export const OUTPUT_RESOLUTIONS: OutputResolution[] = ['720p', '1080p', '2160p'];
export const RESOLUTION_PIXELS: Record<OutputResolution, number> = { '720p': 720, '1080p': 1080, '2160p': 2160 };

export interface OutputSettings {
  resolution: OutputResolution;
  aspect: OutputAspect;
  /** Frames per second; null keeps the source's. */
  fps: number | null;
}

/** A kept stretch of the source, in source time. */
export interface Segment {
  id: string;
  inMs: number;
  outMs: number;
  /** 1 = as recorded; 2 = twice as fast; 0.5 = half speed (sound keeps its pitch). */
  speed: number;
}

/** A camera switch in a multicam cut: from `atMs` on the set's timeline, show this angle. */
export interface MulticamCut {
  atMs: number;
  angleKey: AngleKey;
}

export type Anchor =
  | 'top-left' | 'top' | 'top-right'
  | 'left' | 'center' | 'right'
  | 'bottom-left' | 'bottom' | 'bottom-right';
export const ANCHORS: Anchor[] = ['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right'];

/**
 * Where something sits on the frame. The anchor names a corner, an edge or the
 * middle, `marginPct` keeps it off the edge (a share of the frame's height). When
 * both `xPct` and `yPct` are given, they place the element's centre instead.
 */
export interface Position {
  anchor: Anchor;
  marginPct: number;
  xPct: number | null;
  yPct: number | null;
}

/** A picture (the logo, a frame, a sticker) over the video. */
export interface Overlay {
  id: string;
  assetId: string;
  position: Position;
  widthPct: number;
  /** 0 (invisible) to 1 (solid). */
  opacity: number;
  /** Output time; null = from the start / to the end. */
  fromMs: number | null;
  toMs: number | null;
}

export type TextStyle = 'title' | 'lowerThird' | 'plain';
export const TEXT_STYLES: TextStyle[] = ['title', 'lowerThird', 'plain'];
export type FontFamily = 'sans' | 'serif' | 'mono';
export const FONT_FAMILIES: FontFamily[] = ['sans', 'serif', 'mono'];

/** A name, a title or a line of text on the picture. */
export interface TextOverlay {
  id: string;
  text: string;
  /** A second, smaller line under the first (a lower-third's role or handle). */
  secondaryText: string | null;
  style: TextStyle;
  font: FontFamily;
  position: Position;
  /** Height of the main line as a share of the frame's height. */
  sizePct: number;
  color: string;
  /** Behind the text; null for none. */
  backgroundColor: string | null;
  /** A fade in and out of this length; 0 = cut in and out. */
  fadeMs: number;
  fromMs: number;
  toMs: number;
}

export type CaptionsMode = 'burn' | 'soft';
export type CaptionsPlacement = 'bottom' | 'top';

export interface CaptionsStyle {
  font: FontFamily;
  sizePct: number;
  color: string;
  backgroundColor: string | null;
  placement: CaptionsPlacement;
  /** Characters per caption line before it wraps. */
  maxCharsPerLine: number;
  maxLines: number;
}

/** Captions from the video's transcript, or from a caption file the creator uploaded. */
export interface Captions {
  source: 'transcript' | 'asset';
  /** The caption file (VTT or SRT), when source is 'asset'. */
  assetId: string | null;
  /** 'burn': drawn into the picture; 'soft': a caption track and a sidecar file. */
  mode: CaptionsMode;
  /** A language the transcript should be in, for the record; null = as transcribed. */
  language: string | null;
  style: CaptionsStyle;
}

/** Background music under the result. */
export interface Music {
  assetId: string;
  /** Output time it starts at. */
  startMs: number;
  gainDb: number;
  /** Lower the music while people speak. */
  ducking: boolean;
  /** How far it drops under speech. */
  duckDb: number;
  fadeInMs: number;
  fadeOutMs: number;
  /** Repeat the track to the end of the result. */
  loop: boolean;
}

export type VoiceoverSource =
  | { kind: 'asset'; assetId: string }
  | {
      kind: 'tts';
      text: string;
      voice: string;
      /** Set once the speech is synthesised and saved; the renderer needs it. */
      assetId: string | null;
    };

/** A recorded or spoken line placed on the result. */
export interface Voiceover {
  id: string;
  source: VoiceoverSource;
  atMs: number;
  gainDb: number;
  /** Lower the original sound (and the music) while this plays. */
  duckOriginal: boolean;
}

export interface AudioSettings {
  /** The source's own sound; -60 is silence. */
  originalGainDb: number;
  /** Even out loudness at the end (EBU R128 -16 LUFS). */
  normalize: boolean;
}

export type ReframeMode = 'crop' | 'pad';

/** Turn a 16:9 recording into 9:16, 1:1 or 4:5, around a point of interest. */
export interface Reframe {
  /** The point kept in frame, as shares of the source's width and height. */
  focusXPct: number;
  focusYPct: number;
  /** 'crop' fills the new frame; 'pad' fits the whole picture with bars. */
  mode: ReframeMode;
}

export interface EditList {
  version: typeof EDIT_LIST_VERSION;
  source: EditSource;
  /** The source's length when the list was last saved; every time is checked against it. */
  sourceDurationMs: number;
  output: OutputSettings;
  segments: Segment[];
  /** Multicam only: which angle shows when. Empty = the set's master angle throughout. */
  multicamCut: MulticamCut[];
  overlays: Overlay[];
  texts: TextOverlay[];
  captions: Captions | null;
  music: Music | null;
  voiceovers: Voiceover[];
  audio: AudioSettings;
  reframe: Reframe | null;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_POSITION: Position = { anchor: 'top-right', marginPct: 3, xPct: null, yPct: null };

export const DEFAULT_CAPTIONS_STYLE: CaptionsStyle = {
  font: 'sans',
  sizePct: 4.5,
  color: '#FFFFFF',
  backgroundColor: '#000000A6',
  placement: 'bottom',
  maxCharsPerLine: 42,
  maxLines: 2,
};

export const DEFAULT_OUTPUT: OutputSettings = { resolution: '1080p', aspect: 'source', fps: null };
export const DEFAULT_AUDIO: AudioSettings = { originalGainDb: 0, normalize: true };

/** A list that plays the whole source as it is. */
export function emptyEditList(source: EditSource, sourceDurationMs: number): EditList {
  return {
    version: EDIT_LIST_VERSION,
    source,
    sourceDurationMs,
    output: { ...DEFAULT_OUTPUT },
    segments: [],
    multicamCut: [],
    overlays: [],
    texts: [],
    captions: null,
    music: null,
    voiceovers: [],
    audio: { ...DEFAULT_AUDIO },
    reframe: null,
  };
}

// ---------------------------------------------------------------------------
// Derived timing (shared by the preview, the compiler and the AI prompt)
// ---------------------------------------------------------------------------

/** The stretches that play, in order: the segments, or the whole source when there are none. */
export function playedSegments(list: Pick<EditList, 'segments' | 'sourceDurationMs'>): Segment[] {
  if (list.segments.length > 0) return list.segments;
  return [{ id: 'all', inMs: 0, outMs: list.sourceDurationMs, speed: 1 }];
}

/** How long a segment plays for, after its speed. */
export function segmentOutputMs(segment: Segment): number {
  return Math.round((segment.outMs - segment.inMs) / segment.speed);
}

/** The result's length in output time. */
export function outputDurationMs(list: Pick<EditList, 'segments' | 'sourceDurationMs'>): number {
  return playedSegments(list).reduce((total, segment) => total + segmentOutputMs(segment), 0);
}

/** Where a segment starts and ends on the output timeline. */
export function segmentPlacements(list: Pick<EditList, 'segments' | 'sourceDurationMs'>): Array<{ segment: Segment; startMs: number; endMs: number }> {
  let at = 0;
  return playedSegments(list).map((segment) => {
    const startMs = at;
    at += segmentOutputMs(segment);
    return { segment, startMs, endMs: at };
  });
}

/** The source time playing at an output time, or null when it's past the end. */
export function sourceTimeAt(list: Pick<EditList, 'segments' | 'sourceDurationMs'>, outputMs: number): number | null {
  const placements = segmentPlacements(list);
  for (let i = 0; i < placements.length; i += 1) {
    const { segment, startMs, endMs } = placements[i];
    if (outputMs >= startMs && (outputMs < endMs || (i === placements.length - 1 && outputMs <= endMs))) {
      return segment.inMs + (outputMs - startMs) * segment.speed;
    }
  }
  return null;
}

/**
 * The output time a source time plays at, or null when that stretch was cut.
 * When the same source time is kept twice, the first use wins.
 */
export function outputTimeAt(list: Pick<EditList, 'segments' | 'sourceDurationMs'>, sourceMs: number): number | null {
  const placements = segmentPlacements(list);
  for (let i = 0; i < placements.length; i += 1) {
    const { segment, startMs } = placements[i];
    if (sourceMs >= segment.inMs && sourceMs < segment.outMs) {
      return startMs + (sourceMs - segment.inMs) / segment.speed;
    }
  }
  return null;
}

/** The angle on screen at a multicam timeline time: the latest cut at or before it, else the master. */
export function angleAt(cuts: MulticamCut[], timelineMs: number, master: AngleKey = 'A'): AngleKey {
  let angle = master;
  for (let i = 0; i < cuts.length; i += 1) {
    if (cuts[i].atMs <= timelineMs) angle = cuts[i].angleKey;
    else break;
  }
  return angle;
}

// ---------------------------------------------------------------------------
// Validation and normalisation
// ---------------------------------------------------------------------------

export interface EditListIssue {
  /** JSON path of the offending value: "segments[2].outMs". */
  path: string;
  message: string;
}

export interface KnownContext {
  /** Asset ids the account owns; an id outside it is refused. Omit to skip the check. */
  assetIds?: string[];
  /** The set's angles; a cut to another angle is refused. Omit to skip the check. */
  angleKeys?: AngleKey[];
}

export type NormalizeResult = { ok: true; value: EditList } | { ok: false; issues: EditListIssue[] };

class Issues {
  readonly list: EditListIssue[] = [];
  add(path: string, message: string): void {
    this.list.push({ path, message });
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && isFinite(value);

function readNumber(issues: Issues, path: string, value: unknown, options: { min?: number; max?: number; integer?: boolean; fallback?: number }): number {
  if (value === undefined || value === null) {
    if (options.fallback !== undefined) return options.fallback;
    issues.add(path, 'is missing');
    return 0;
  }
  if (!isFiniteNumber(value)) {
    issues.add(path, 'must be a number');
    return options.fallback ?? 0;
  }
  let result = value;
  if (options.integer) result = Math.round(result);
  if (options.min !== undefined && result < options.min) {
    issues.add(path, `must be at least ${options.min}`);
    return options.min;
  }
  if (options.max !== undefined && result > options.max) {
    issues.add(path, `must be at most ${options.max}`);
    return options.max;
  }
  return result;
}

function readOptionalNumber(issues: Issues, path: string, value: unknown, options: { min?: number; max?: number; integer?: boolean }): number | null {
  if (value === undefined || value === null) return null;
  return readNumber(issues, path, value, options);
}

function readBoolean(issues: Issues, path: string, value: unknown, fallback: boolean): boolean {
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'boolean') {
    issues.add(path, 'must be true or false');
    return fallback;
  }
  return value;
}

/** Control characters (except newlines and tabs) never belong in a caption or a title. */
export function stripControlCharacters(value: string): string {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    const control = (code < 32 && code !== 9 && code !== 10 && code !== 13) || code === 127;
    if (!control) out += value.charAt(i);
  }
  return out;
}

function readString(issues: Issues, path: string, value: unknown, options: { maxLength: number; required?: boolean; fallback?: string }): string {
  if (value === undefined || value === null) {
    if (options.required) issues.add(path, 'is missing');
    return options.fallback ?? '';
  }
  if (typeof value !== 'string') {
    issues.add(path, 'must be text');
    return options.fallback ?? '';
  }
  const cleaned = stripControlCharacters(value);
  if (cleaned.length > options.maxLength) {
    issues.add(path, `must be ${options.maxLength} characters or fewer`);
    return cleaned.slice(0, options.maxLength);
  }
  return cleaned;
}

function readChoice<T extends string>(issues: Issues, path: string, value: unknown, choices: T[], fallback: T): T {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string' && choices.indexOf(value as T) >= 0) return value as T;
  issues.add(path, `must be one of ${choices.join(', ')}`);
  return fallback;
}

function readId(issues: Issues, path: string, value: unknown, seen: Record<string, true>): string {
  if (typeof value !== 'string' || !EDIT_LIMITS.idPattern.test(value)) {
    issues.add(path, 'must be 1–32 letters, digits, "_" or "-"');
    return '';
  }
  if (seen[value]) issues.add(path, `"${value}" is used twice`);
  seen[value] = true;
  return value;
}

function readColor(issues: Issues, path: string, value: unknown, fallback: string | null): string | null {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string' && EDIT_LIMITS.colorPattern.test(value)) return value.toUpperCase();
  issues.add(path, 'must be a colour like #RRGGBB or #RRGGBBAA');
  return fallback;
}

function readAssetId(issues: Issues, path: string, value: unknown, known: KnownContext): string {
  if (typeof value !== 'string' || !EDIT_LIMITS.idPattern.test(value)) {
    issues.add(path, 'must name an asset');
    return '';
  }
  if (known.assetIds && known.assetIds.indexOf(value) < 0) {
    issues.add(path, 'names an asset this account doesn’t have');
  }
  return value;
}

function readPosition(issues: Issues, path: string, value: unknown, fallback: Position): Position {
  if (value === undefined || value === null) return { ...fallback };
  if (!isRecord(value)) {
    issues.add(path, 'must be an object');
    return { ...fallback };
  }
  const xPct = readOptionalNumber(issues, `${path}.xPct`, value.xPct, { min: 0, max: 100 });
  const yPct = readOptionalNumber(issues, `${path}.yPct`, value.yPct, { min: 0, max: 100 });
  if ((xPct === null) !== (yPct === null)) issues.add(path, 'needs both xPct and yPct, or neither');
  return {
    anchor: readChoice(issues, `${path}.anchor`, value.anchor, ANCHORS, fallback.anchor),
    marginPct: readNumber(issues, `${path}.marginPct`, value.marginPct, { min: 0, max: 40, fallback: fallback.marginPct }),
    xPct: xPct !== null && yPct !== null ? xPct : null,
    yPct: xPct !== null && yPct !== null ? yPct : null,
  };
}

function readTimeRange(issues: Issues, path: string, from: unknown, to: unknown, maxMs: number, required: boolean): { fromMs: number | null; toMs: number | null } {
  const fromMs = required
    ? readNumber(issues, `${path}.fromMs`, from, { min: 0, max: maxMs, integer: true })
    : readOptionalNumber(issues, `${path}.fromMs`, from, { min: 0, max: maxMs, integer: true });
  const toMs = required
    ? readNumber(issues, `${path}.toMs`, to, { min: 0, max: maxMs, integer: true })
    : readOptionalNumber(issues, `${path}.toMs`, to, { min: 0, max: maxMs, integer: true });
  if (fromMs !== null && toMs !== null && toMs <= fromMs) issues.add(`${path}.toMs`, 'must be after fromMs');
  return { fromMs, toMs };
}

function readList<T>(issues: Issues, path: string, value: unknown, max: number, read: (item: unknown, itemPath: string, index: number) => T): T[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    issues.add(path, 'must be a list');
    return [];
  }
  if (value.length > max) issues.add(path, `can hold at most ${max} items`);
  return value.slice(0, max).map((item, index) => read(item, `${path}[${index}]`, index));
}

function readSource(issues: Issues, value: unknown): EditSource {
  if (!isRecord(value)) {
    issues.add('source', 'is missing');
    return { kind: 'video', videoId: '' };
  }
  if (value.kind === 'multicam') {
    const multicamSetId = typeof value.multicamSetId === 'string' && value.multicamSetId ? value.multicamSetId : '';
    if (!multicamSetId) issues.add('source.multicamSetId', 'is missing');
    return { kind: 'multicam', multicamSetId };
  }
  if (value.kind !== 'video') issues.add('source.kind', 'must be "video" or "multicam"');
  const videoId = typeof value.videoId === 'string' && value.videoId ? value.videoId : '';
  if (!videoId) issues.add('source.videoId', 'is missing');
  return { kind: 'video', videoId };
}

/**
 * Checks a document from the app, the AI or the database and returns it in
 * canonical form (every default filled, numbers rounded where they are times,
 * colours upper-case), or every problem found, with the path of each. Nothing
 * is silently dropped: a value out of range is an issue, not a clamp.
 */
export function normalizeEditList(input: unknown, known: KnownContext = {}): NormalizeResult {
  const issues = new Issues();
  if (!isRecord(input)) return { ok: false, issues: [{ path: '', message: 'must be an object' }] };

  if (input.version !== EDIT_LIST_VERSION) issues.add('version', `must be ${EDIT_LIST_VERSION}`);
  const source = readSource(issues, input.source);
  const sourceDurationMs = readNumber(issues, 'sourceDurationMs', input.sourceDurationMs, { min: 1, max: EDIT_LIMITS.maxSourceMs, integer: true });

  const outputIn = isRecord(input.output) ? input.output : {};
  if (input.output !== undefined && !isRecord(input.output)) issues.add('output', 'must be an object');
  const output: OutputSettings = {
    resolution: readChoice(issues, 'output.resolution', outputIn.resolution, OUTPUT_RESOLUTIONS, DEFAULT_OUTPUT.resolution),
    aspect: readChoice(issues, 'output.aspect', outputIn.aspect, OUTPUT_ASPECTS, DEFAULT_OUTPUT.aspect),
    fps: readOptionalNumber(issues, 'output.fps', outputIn.fps, { min: 1, max: 120 }),
  };

  const segmentIds: Record<string, true> = {};
  const segments = readList<Segment>(issues, 'segments', input.segments, EDIT_LIMITS.maxSegments, (item, path) => {
    if (!isRecord(item)) {
      issues.add(path, 'must be an object');
      return { id: '', inMs: 0, outMs: 0, speed: 1 };
    }
    const inMs = readNumber(issues, `${path}.inMs`, item.inMs, { min: 0, max: sourceDurationMs, integer: true });
    const outMs = readNumber(issues, `${path}.outMs`, item.outMs, { min: 0, max: sourceDurationMs, integer: true });
    if (outMs - inMs < EDIT_LIMITS.minSegmentMs) issues.add(`${path}.outMs`, `must be at least ${EDIT_LIMITS.minSegmentMs} ms after inMs`);
    return {
      id: readId(issues, `${path}.id`, item.id, segmentIds),
      inMs,
      outMs,
      speed: readNumber(issues, `${path}.speed`, item.speed, { min: EDIT_LIMITS.speed.min, max: EDIT_LIMITS.speed.max, fallback: 1 }),
    };
  });

  const multicamCut = readList<MulticamCut>(issues, 'multicamCut', input.multicamCut, EDIT_LIMITS.maxMulticamCuts, (item, path) => {
    if (!isRecord(item)) {
      issues.add(path, 'must be an object');
      return { atMs: 0, angleKey: 'A' };
    }
    const angleKey = readChoice(issues, `${path}.angleKey`, item.angleKey, ANGLE_KEYS, 'A');
    if (known.angleKeys && known.angleKeys.indexOf(angleKey) < 0) issues.add(`${path}.angleKey`, `the set has no angle ${angleKey}`);
    return { atMs: readNumber(issues, `${path}.atMs`, item.atMs, { min: 0, max: sourceDurationMs, integer: true }), angleKey };
  });
  if (source.kind !== 'multicam' && multicamCut.length > 0) issues.add('multicamCut', 'only a multicam edit has camera cuts');
  for (let i = 1; i < multicamCut.length; i += 1) {
    if (multicamCut[i].atMs <= multicamCut[i - 1].atMs) {
      issues.add(`multicamCut[${i}].atMs`, 'cuts must be in time order, one per moment');
      break;
    }
  }

  const durationMs = outputDurationMs({ segments, sourceDurationMs });
  const outputMax = Math.max(durationMs, 1);

  const overlayIds: Record<string, true> = {};
  const overlays = readList<Overlay>(issues, 'overlays', input.overlays, EDIT_LIMITS.maxOverlays, (item, path) => {
    if (!isRecord(item)) {
      issues.add(path, 'must be an object');
      return { id: '', assetId: '', position: { ...DEFAULT_POSITION }, widthPct: 15, opacity: 1, fromMs: null, toMs: null };
    }
    const range = readTimeRange(issues, path, item.fromMs, item.toMs, outputMax, false);
    return {
      id: readId(issues, `${path}.id`, item.id, overlayIds),
      assetId: readAssetId(issues, `${path}.assetId`, item.assetId, known),
      position: readPosition(issues, `${path}.position`, item.position, DEFAULT_POSITION),
      widthPct: readNumber(issues, `${path}.widthPct`, item.widthPct, { min: EDIT_LIMITS.overlayWidthPct.min, max: EDIT_LIMITS.overlayWidthPct.max, fallback: 15 }),
      opacity: readNumber(issues, `${path}.opacity`, item.opacity, { min: 0, max: 1, fallback: 1 }),
      fromMs: range.fromMs,
      toMs: range.toMs,
    };
  });

  const textIds: Record<string, true> = {};
  const texts = readList<TextOverlay>(issues, 'texts', input.texts, EDIT_LIMITS.maxTexts, (item, path) => {
    const blank: TextOverlay = {
      id: '', text: '', secondaryText: null, style: 'plain', font: 'sans', position: { anchor: 'bottom', marginPct: 8, xPct: null, yPct: null },
      sizePct: 5, color: '#FFFFFF', backgroundColor: null, fadeMs: 0, fromMs: 0, toMs: 0,
    };
    if (!isRecord(item)) {
      issues.add(path, 'must be an object');
      return blank;
    }
    const style = readChoice(issues, `${path}.style`, item.style, TEXT_STYLES, 'plain');
    const defaults = style === 'title'
      ? { position: { anchor: 'center' as Anchor, marginPct: 0, xPct: null, yPct: null }, sizePct: 8 }
      : style === 'lowerThird'
        ? { position: { anchor: 'bottom-left' as Anchor, marginPct: 8, xPct: null, yPct: null }, sizePct: 5 }
        : { position: { anchor: 'bottom' as Anchor, marginPct: 8, xPct: null, yPct: null }, sizePct: 5 };
    const text = readString(issues, `${path}.text`, item.text, { maxLength: EDIT_LIMITS.maxTextLength, required: true });
    if (!text.trim()) issues.add(`${path}.text`, 'is empty');
    const secondary = readString(issues, `${path}.secondaryText`, item.secondaryText, { maxLength: EDIT_LIMITS.maxTextLength });
    const range = readTimeRange(issues, path, item.fromMs, item.toMs, outputMax, true);
    return {
      id: readId(issues, `${path}.id`, item.id, textIds),
      text,
      secondaryText: secondary.trim() ? secondary : null,
      style,
      font: readChoice(issues, `${path}.font`, item.font, FONT_FAMILIES, 'sans'),
      position: readPosition(issues, `${path}.position`, item.position, defaults.position),
      sizePct: readNumber(issues, `${path}.sizePct`, item.sizePct, { min: EDIT_LIMITS.textSizePct.min, max: EDIT_LIMITS.textSizePct.max, fallback: defaults.sizePct }),
      color: readColor(issues, `${path}.color`, item.color, '#FFFFFF') as string,
      backgroundColor: readColor(issues, `${path}.backgroundColor`, item.backgroundColor, null),
      fadeMs: readNumber(issues, `${path}.fadeMs`, item.fadeMs, { min: 0, max: EDIT_LIMITS.maxFadeMs, integer: true, fallback: style === 'plain' ? 0 : 300 }),
      fromMs: range.fromMs as number,
      toMs: range.toMs as number,
    };
  });

  let captions: Captions | null = null;
  if (input.captions !== undefined && input.captions !== null) {
    if (!isRecord(input.captions)) {
      issues.add('captions', 'must be an object or null');
    } else {
      const c = input.captions;
      const source = readChoice(issues, 'captions.source', c.source, ['transcript', 'asset'] as Array<'transcript' | 'asset'>, 'transcript');
      const styleIn = isRecord(c.style) ? c.style : {};
      captions = {
        source,
        assetId: source === 'asset' ? readAssetId(issues, 'captions.assetId', c.assetId, known) : null,
        mode: readChoice(issues, 'captions.mode', c.mode, ['burn', 'soft'] as CaptionsMode[], 'burn'),
        language: typeof c.language === 'string' && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/.test(c.language) ? c.language : null,
        style: {
          font: readChoice(issues, 'captions.style.font', styleIn.font, FONT_FAMILIES, DEFAULT_CAPTIONS_STYLE.font),
          sizePct: readNumber(issues, 'captions.style.sizePct', styleIn.sizePct, { min: EDIT_LIMITS.textSizePct.min, max: 12, fallback: DEFAULT_CAPTIONS_STYLE.sizePct }),
          color: readColor(issues, 'captions.style.color', styleIn.color, DEFAULT_CAPTIONS_STYLE.color) as string,
          backgroundColor: styleIn.backgroundColor === null ? null : readColor(issues, 'captions.style.backgroundColor', styleIn.backgroundColor, DEFAULT_CAPTIONS_STYLE.backgroundColor),
          placement: readChoice(issues, 'captions.style.placement', styleIn.placement, ['bottom', 'top'] as CaptionsPlacement[], 'bottom'),
          maxCharsPerLine: readNumber(issues, 'captions.style.maxCharsPerLine', styleIn.maxCharsPerLine, { min: 16, max: 80, integer: true, fallback: DEFAULT_CAPTIONS_STYLE.maxCharsPerLine }),
          maxLines: readNumber(issues, 'captions.style.maxLines', styleIn.maxLines, { min: 1, max: 3, integer: true, fallback: DEFAULT_CAPTIONS_STYLE.maxLines }),
        },
      };
      if (c.language !== undefined && c.language !== null && captions.language === null) issues.add('captions.language', 'must be a language code like "en" or "pt-BR"');
    }
  }

  let music: Music | null = null;
  if (input.music !== undefined && input.music !== null) {
    if (!isRecord(input.music)) {
      issues.add('music', 'must be an object or null');
    } else {
      const m = input.music;
      music = {
        assetId: readAssetId(issues, 'music.assetId', m.assetId, known),
        startMs: readNumber(issues, 'music.startMs', m.startMs, { min: 0, max: outputMax, integer: true, fallback: 0 }),
        gainDb: readNumber(issues, 'music.gainDb', m.gainDb, { min: EDIT_LIMITS.gainDb.min, max: EDIT_LIMITS.gainDb.max, fallback: -14 }),
        ducking: readBoolean(issues, 'music.ducking', m.ducking, true),
        duckDb: readNumber(issues, 'music.duckDb', m.duckDb, { min: EDIT_LIMITS.duckDb.min, max: EDIT_LIMITS.duckDb.max, fallback: 12 }),
        fadeInMs: readNumber(issues, 'music.fadeInMs', m.fadeInMs, { min: 0, max: EDIT_LIMITS.maxFadeMs, integer: true, fallback: 1000 }),
        fadeOutMs: readNumber(issues, 'music.fadeOutMs', m.fadeOutMs, { min: 0, max: EDIT_LIMITS.maxFadeMs, integer: true, fallback: 2000 }),
        loop: readBoolean(issues, 'music.loop', m.loop, true),
      };
    }
  }

  const voiceoverIds: Record<string, true> = {};
  const voiceovers = readList<Voiceover>(issues, 'voiceovers', input.voiceovers, EDIT_LIMITS.maxVoiceovers, (item, path) => {
    const blank: Voiceover = { id: '', source: { kind: 'asset', assetId: '' }, atMs: 0, gainDb: 0, duckOriginal: true };
    if (!isRecord(item)) {
      issues.add(path, 'must be an object');
      return blank;
    }
    let source: VoiceoverSource = { kind: 'asset', assetId: '' };
    if (!isRecord(item.source)) {
      issues.add(`${path}.source`, 'is missing');
    } else if (item.source.kind === 'tts') {
      const text = readString(issues, `${path}.source.text`, item.source.text, { maxLength: EDIT_LIMITS.maxTtsTextLength, required: true });
      if (!text.trim()) issues.add(`${path}.source.text`, 'is empty');
      const voice = readString(issues, `${path}.source.voice`, item.source.voice, { maxLength: 64, required: true });
      if (!/^[A-Za-z0-9_-]+$/.test(voice)) issues.add(`${path}.source.voice`, 'must name a voice');
      const assetId = item.source.assetId === undefined || item.source.assetId === null ? null : readAssetId(issues, `${path}.source.assetId`, item.source.assetId, known);
      source = { kind: 'tts', text, voice, assetId };
    } else if (item.source.kind === 'asset') {
      source = { kind: 'asset', assetId: readAssetId(issues, `${path}.source.assetId`, item.source.assetId, known) };
    } else {
      issues.add(`${path}.source.kind`, 'must be "asset" or "tts"');
    }
    return {
      id: readId(issues, `${path}.id`, item.id, voiceoverIds),
      source,
      atMs: readNumber(issues, `${path}.atMs`, item.atMs, { min: 0, max: outputMax, integer: true, fallback: 0 }),
      gainDb: readNumber(issues, `${path}.gainDb`, item.gainDb, { min: EDIT_LIMITS.gainDb.min, max: EDIT_LIMITS.gainDb.max, fallback: 0 }),
      duckOriginal: readBoolean(issues, `${path}.duckOriginal`, item.duckOriginal, true),
    };
  });

  const audioIn = isRecord(input.audio) ? input.audio : {};
  if (input.audio !== undefined && input.audio !== null && !isRecord(input.audio)) issues.add('audio', 'must be an object');
  const audio: AudioSettings = {
    originalGainDb: readNumber(issues, 'audio.originalGainDb', audioIn.originalGainDb, { min: EDIT_LIMITS.gainDb.min, max: EDIT_LIMITS.gainDb.max, fallback: 0 }),
    normalize: readBoolean(issues, 'audio.normalize', audioIn.normalize, true),
  };

  let reframe: Reframe | null = null;
  if (input.reframe !== undefined && input.reframe !== null) {
    if (!isRecord(input.reframe)) {
      issues.add('reframe', 'must be an object or null');
    } else {
      reframe = {
        focusXPct: readNumber(issues, 'reframe.focusXPct', input.reframe.focusXPct, { min: 0, max: 100, fallback: 50 }),
        focusYPct: readNumber(issues, 'reframe.focusYPct', input.reframe.focusYPct, { min: 0, max: 100, fallback: 50 }),
        mode: readChoice(issues, 'reframe.mode', input.reframe.mode, ['crop', 'pad'] as ReframeMode[], 'crop'),
      };
    }
  }

  if (issues.list.length > 0) return { ok: false, issues: issues.list };
  return {
    ok: true,
    value: { version: EDIT_LIST_VERSION, source, sourceDurationMs, output, segments, multicamCut, overlays, texts, captions, music, voiceovers, audio, reframe },
  };
}

/** Every asset id an edit list refers to, each once. */
export function referencedAssetIds(list: EditList): string[] {
  const ids: string[] = [];
  const add = (id: string | null | undefined) => {
    if (id && ids.indexOf(id) < 0) ids.push(id);
  };
  list.overlays.forEach((overlay) => add(overlay.assetId));
  if (list.captions) add(list.captions.assetId);
  if (list.music) add(list.music.assetId);
  list.voiceovers.forEach((voiceover) => add(voiceover.source.assetId));
  return ids;
}

/** A one-line summary for lists and logs: "3 segments, logo, 2 texts, captions, music". */
export function describeEditList(list: EditList): string {
  const parts: string[] = [];
  const segments = list.segments.length;
  parts.push(segments === 0 ? 'whole video' : `${segments} segment${segments === 1 ? '' : 's'}`);
  if (list.multicamCut.length) parts.push(`${list.multicamCut.length} camera cut${list.multicamCut.length === 1 ? '' : 's'}`);
  if (list.overlays.length) parts.push(list.overlays.length === 1 ? 'a picture' : `${list.overlays.length} pictures`);
  if (list.texts.length) parts.push(list.texts.length === 1 ? 'a text' : `${list.texts.length} texts`);
  if (list.captions) parts.push(list.captions.mode === 'burn' ? 'captions' : 'soft captions');
  if (list.music) parts.push('music');
  if (list.voiceovers.length) parts.push(list.voiceovers.length === 1 ? 'a voiceover' : `${list.voiceovers.length} voiceovers`);
  if (list.reframe || list.output.aspect !== 'source') parts.push(list.output.aspect);
  if (list.segments.some((segment) => segment.speed !== 1)) parts.push('speed changes');
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// JSON Schema (documentation, the app's checks, and the AI's structured output)
// ---------------------------------------------------------------------------

type JsonSchema = Record<string, unknown>;

const nullable = (schema: JsonSchema): JsonSchema => ({ anyOf: [schema, { type: 'null' }] });
const number = (min: number, max: number, description?: string): JsonSchema => ({ type: 'number', minimum: min, maximum: max, ...(description ? { description } : {}) });
const integer = (min: number, max: number, description?: string): JsonSchema => ({ type: 'integer', minimum: min, maximum: max, ...(description ? { description } : {}) });
const enumOf = (values: string[], description?: string): JsonSchema => ({ type: 'string', enum: values, ...(description ? { description } : {}) });
const object = (properties: Record<string, JsonSchema>, description?: string): JsonSchema => ({
  type: 'object',
  properties,
  required: Object.keys(properties),
  additionalProperties: false,
  ...(description ? { description } : {}),
});
const list = (items: JsonSchema, maxItems: number, description?: string): JsonSchema => ({ type: 'array', items, maxItems, ...(description ? { description } : {}) });

const ID_SCHEMA: JsonSchema = { type: 'string', pattern: '^[A-Za-z0-9_-]{1,32}$' };
const COLOR_SCHEMA: JsonSchema = { type: 'string', pattern: '^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$', description: 'A colour: #RRGGBB or #RRGGBBAA' };
const MS = EDIT_LIMITS.maxSourceMs;

const POSITION_SCHEMA = object({
  anchor: enumOf(ANCHORS, 'The corner, edge or middle the element sits at'),
  marginPct: number(0, 40, 'Distance from the edge, as a share of the frame height'),
  xPct: nullable(number(0, 100, 'With yPct: the element’s centre, as a share of the width; otherwise null')),
  yPct: nullable(number(0, 100)),
}, 'Where something sits on the frame');

/**
 * The edit list as JSON Schema (draft 2020-12). Every property is required and
 * nothing extra is allowed, the form OpenAI's structured outputs need; optional
 * values are nullable. `normalizeEditList` is the authority: this schema is
 * generated from the same limits and the test suite checks they agree.
 */
export const EDIT_LIST_JSON_SCHEMA: JsonSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  title: 'PithPlay edit list',
  description: 'An edit of one video or multicam set. Segments are in source milliseconds; everything else is in output (result) milliseconds.',
  ...object({
    version: { type: 'integer', const: EDIT_LIST_VERSION },
    source: {
      anyOf: [
        object({ kind: { type: 'string', const: 'video' }, videoId: { type: 'string', minLength: 1 } }),
        object({ kind: { type: 'string', const: 'multicam' }, multicamSetId: { type: 'string', minLength: 1 } }),
      ],
    },
    sourceDurationMs: integer(1, MS, 'The source’s length'),
    output: object({
      resolution: enumOf(OUTPUT_RESOLUTIONS),
      aspect: enumOf(OUTPUT_ASPECTS, '"source" keeps the source’s proportions'),
      fps: nullable(number(1, 120)),
    }),
    segments: list(object({
      id: ID_SCHEMA,
      inMs: integer(0, MS, 'Source time the stretch starts'),
      outMs: integer(0, MS, 'Source time it ends (exclusive)'),
      speed: number(EDIT_LIMITS.speed.min, EDIT_LIMITS.speed.max, '1 = as recorded'),
    }), EDIT_LIMITS.maxSegments, 'The kept stretches in output order; empty = the whole source'),
    multicamCut: list(object({ atMs: integer(0, MS), angleKey: enumOf(ANGLE_KEYS) }), EDIT_LIMITS.maxMulticamCuts, 'Multicam only: camera switches on the set’s timeline, in time order'),
    overlays: list(object({
      id: ID_SCHEMA,
      assetId: ID_SCHEMA,
      position: POSITION_SCHEMA,
      widthPct: number(EDIT_LIMITS.overlayWidthPct.min, EDIT_LIMITS.overlayWidthPct.max, 'Width as a share of the frame width'),
      opacity: number(0, 1),
      fromMs: nullable(integer(0, MS, 'Output time; null = from the start')),
      toMs: nullable(integer(0, MS, 'Output time; null = to the end')),
    }), EDIT_LIMITS.maxOverlays, 'Pictures (the logo) over the video'),
    texts: list(object({
      id: ID_SCHEMA,
      text: { type: 'string', minLength: 1, maxLength: EDIT_LIMITS.maxTextLength },
      secondaryText: nullable({ type: 'string', maxLength: EDIT_LIMITS.maxTextLength, description: 'A smaller second line (a lower-third’s role)' }),
      style: enumOf(TEXT_STYLES),
      font: enumOf(FONT_FAMILIES),
      position: POSITION_SCHEMA,
      sizePct: number(EDIT_LIMITS.textSizePct.min, EDIT_LIMITS.textSizePct.max, 'Text height as a share of the frame height'),
      color: COLOR_SCHEMA,
      backgroundColor: nullable(COLOR_SCHEMA),
      fadeMs: integer(0, EDIT_LIMITS.maxFadeMs),
      fromMs: integer(0, MS, 'Output time'),
      toMs: integer(0, MS, 'Output time'),
    }), EDIT_LIMITS.maxTexts, 'Names, titles and lines of text'),
    captions: nullable(object({
      source: enumOf(['transcript', 'asset']),
      assetId: nullable(ID_SCHEMA),
      mode: enumOf(['burn', 'soft'], '"burn" draws them into the picture; "soft" adds a caption track'),
      language: nullable({ type: 'string', pattern: '^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$' }),
      style: object({
        font: enumOf(FONT_FAMILIES),
        sizePct: number(EDIT_LIMITS.textSizePct.min, 12),
        color: COLOR_SCHEMA,
        backgroundColor: nullable(COLOR_SCHEMA),
        placement: enumOf(['bottom', 'top']),
        maxCharsPerLine: integer(16, 80),
        maxLines: integer(1, 3),
      }),
    })),
    music: nullable(object({
      assetId: ID_SCHEMA,
      startMs: integer(0, MS, 'Output time it starts at'),
      gainDb: number(EDIT_LIMITS.gainDb.min, EDIT_LIMITS.gainDb.max, '-14 is a typical bed under speech'),
      ducking: { type: 'boolean', description: 'Lower the music while people speak' },
      duckDb: number(EDIT_LIMITS.duckDb.min, EDIT_LIMITS.duckDb.max, 'How far it drops under speech'),
      fadeInMs: integer(0, EDIT_LIMITS.maxFadeMs),
      fadeOutMs: integer(0, EDIT_LIMITS.maxFadeMs),
      loop: { type: 'boolean' },
    })),
    voiceovers: list(object({
      id: ID_SCHEMA,
      source: {
        anyOf: [
          object({ kind: { type: 'string', const: 'asset' }, assetId: ID_SCHEMA }),
          object({
            kind: { type: 'string', const: 'tts' },
            text: { type: 'string', minLength: 1, maxLength: EDIT_LIMITS.maxTtsTextLength },
            voice: { type: 'string', pattern: '^[A-Za-z0-9_-]{1,64}$' },
            assetId: nullable(ID_SCHEMA),
          }),
        ],
      },
      atMs: integer(0, MS, 'Output time it starts at'),
      gainDb: number(EDIT_LIMITS.gainDb.min, EDIT_LIMITS.gainDb.max),
      duckOriginal: { type: 'boolean', description: 'Lower the original sound while it plays' },
    }), EDIT_LIMITS.maxVoiceovers),
    audio: object({
      originalGainDb: number(EDIT_LIMITS.gainDb.min, EDIT_LIMITS.gainDb.max, 'The source’s own sound; -60 mutes it'),
      normalize: { type: 'boolean', description: 'Even out loudness at the end' },
    }),
    reframe: nullable(object({
      focusXPct: number(0, 100, 'The point kept in frame, as a share of the source width'),
      focusYPct: number(0, 100),
      mode: enumOf(['crop', 'pad']),
    }, 'How a different aspect is reached; null when the aspect is "source"')),
  }),
};
